import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Component, ElementRef, QueryList, ViewChildren, ChangeDetectorRef, computed, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { LayoutSidebarService } from '../../../shared/services/layout-sidebar.service';
import { OrganizationStoreService } from '../../../shared/services/organization-store.service';
import { FormsModule } from '@angular/forms';
import { UserStoreService } from '../../../shared/services/user-store.service';
import { ApiUserService } from '../../../shared/services/api-user.service';
import { AuthService } from '../../../shared/auth/auth.service';
import { UserRole } from '../../../shared/models/user-role.type';

@Component({
  selector: 'app-sidebar',
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './app-sidebar.component.html',
})
export class AppSidebarComponent {
  openedSubmenu: string | null | number = null;
  subMenuHeights: { [key: string]: number } = {};
  @ViewChildren('subMenu') subMenuRefs!: QueryList<ElementRef>;

  readonly isExpanded;
  readonly isMobileOpen;
  readonly isHovered;
  readonly isFullscreen;
  readonly isFullscreenOpen;
  readonly surveyId;

  private subscription: Subscription = new Subscription();
  private navMenuIds: {id:string, path:string}[] = [
    {id:"dashboard", path:"/"},
    {id:"organizations", path:"/organizations"},
    {id:"surveys", path:"/surveys"},
    {id:"release-management", path:"/release-management"},
    {id:"translations", path:"/translations"},
    {id:"reporting", path:"/reporting"}
  ];

  readonly isSuperAdmin = computed(() => this.userStore.isSuperAdmin());
  readonly isAdmin = computed(() => this.userStore.isAdmin());

  // TEMPORARY, FOR TESTING ONLY: the real logged-in role, used to gate the debug role switcher
  // itself so it stays visible/usable no matter what role is currently being previewed. Remove
  // alongside UserStoreService.setRoleOverride() once role UI work is verified.
  readonly isActualSuperAdmin = computed(() => this.userStore.isActualSuperAdmin());
  readonly debugRoleOptions: UserRole[] = ['SuperAdmin', 'Admin', 'User'];

  changingOrganization = signal(false);
  orgChangeError = signal<string | null>(null);

  constructor(
    public sidebarService: LayoutSidebarService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    public organizationStore: OrganizationStoreService,
    public userStore: UserStoreService,
    private apiUserService: ApiUserService,
    private authService: AuthService,
  ) {
    this.isExpanded = this.sidebarService.isExpanded;
    this.isMobileOpen = this.sidebarService.isMobileOpen;
    this.isHovered = this.sidebarService.isHovered;
    this.isFullscreen = this.sidebarService.isFullscreen;
    this.isFullscreenOpen = this.sidebarService.isFullscreenOpen;
    this.surveyId = this.organizationStore.selectedSurveyId;
  }

  ngOnInit() {
    // Subscribe to router events
    this.subscription.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.setActiveMenuFromRoute(this.router.url);
        }
      })
    );

    // Initial load
    this.setActiveMenuFromRoute(this.router.url);
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.subscription.unsubscribe();
  }

  isActive(path: string): boolean {
    return this.router.url === path;
  }

  toggleSubmenu(id: string) {
    if (this.openedSubmenu === id) {
      this.closeSubmenu(id);
    } else {
      this.openSubmenu(id);
    }
  }

  openSubmenu(id:string) {
    this.openedSubmenu = id;

    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        this.subMenuHeights[id] = el.scrollHeight;
        this.cdr.detectChanges(); // Ensure UI updates
      }
    });
  }

  closeSubmenu(id:string) {
    this.openedSubmenu = null;
    this.subMenuHeights[id] = 0;
  }

  onSidebarMouseEnter() {
    if (!this.isExpanded()) {
      this.sidebarService.setHovered(true);
    }
  }

  private setActiveMenuFromRoute(currentUrl: string) {
    this.navMenuIds.forEach((nav) => {
      if (nav.path) {
        if (this.getRootPath(currentUrl) === nav.path) {
          this.openSubmenu(nav.id);
        }
      }
    });
  }

  onOrganizationChange(id: string): void {
    this.orgChangeError.set(null);
    this.changingOrganization.set(true);

    this.apiUserService.changeOrganization(id).subscribe({
      next: (response) => {
        this.userStore.setActiveOrg(response.activeOrg);

        // The backend maps activeOrg into Keycloak, so the token we're currently holding still
        // carries the old org claim — force a fresh one before navigating so the next page's
        // data requests are authorized against the new org, not the previous one.
        this.authService.refreshToken(-1).finally(() => {
          this.changingOrganization.set(false);
          this.organizationStore.setOrganizationById(id);
          this.router.navigateByUrl('/');
        });
      },
      error: () => {
        this.changingOrganization.set(false);
        this.orgChangeError.set('Failed to switch organization. Please try again.');
      }
    });
  }

  private readonly surveySectionSegments = ['builder', 'translations', 'cmd', 'fieldworks', 'businessrules', 'alert'];

  onSurveyChange(id: string): void {
    this.organizationStore.setSurveyById(id);

    // If already on a survey sub-page, stay on the same section for the newly selected survey instead of bouncing to the surveys list.
    const segments = this.router.url.split('?')[0].split('/').filter(Boolean);
    const section = segments.length === 3 && segments[0] === 'surveys' && this.surveySectionSegments.includes(segments[2]) ? segments[2] : null;
    this.router.navigateByUrl(section ? `/surveys/${id}/${section}` : '/surveys');
  }

  onSubmenuClick() {
    if (this.isMobileOpen()) {
      this.sidebarService.setMobileOpen(false);
    }
  }

  getRootPath(url: string): string {
    const path = url.split('?')[0].split('#')[0];
    const segments = path.split('/').filter(Boolean);
    return segments.length ? '/' + segments[0] : '/';
  }
}
